import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";

// ============================================================
// FYUNOR — Card System ("Monolith Cards")
// Borderless containers defined by soft high-diffusion shadows
// and a 1px "Stroke of Light" inner border (see .monolith).
// ============================================================

const baseCard = "monolith overflow-hidden";

const Card = React.forwardRef(({ className, as: Comp = "div", interactive = false, ...props }, ref) => (
  <Comp
    ref={ref}
    className={cn(
      baseCard,
      interactive && "transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer",
      className
    )}
    {...props}
  />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("font-heading font-semibold leading-tight tracking-[var(--tracking-heading)] text-lg", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm text-muted-foreground leading-relaxed", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
));
CardFooter.displayName = "CardFooter";

// ---- Specialised cards for the calculator ecosystem ----

/** Result panel — the sticky "Result Prism" on the right pane. */
const ResultCard = React.forwardRef(({ className, label, children, footer, ...props }, ref) => (
  <div ref={ref} className={cn(baseCard, "p-6", className)} {...props}>
    {label && <div className="mb-4 text-xs font-medium uppercase tracking-wider text-subtle-foreground">{label}</div>}
    <div className="space-y-4">{children}</div>
    {footer && <div className="mt-6 border-t border-border pt-4">{footer}</div>}
  </div>
));
ResultCard.displayName = "ResultCard";

/** Insight card — highlights a takeaway beneath results. */
const InsightCard = React.forwardRef(({ className, tone = "info", title, children, icon: Icon, ...props }, ref) => {
  const toneRing = {
    info: "text-info",
    success: "text-success",
    warning: "text-warning",
    error: "text-error",
  }[tone];
  return (
    <div ref={ref} className={cn(baseCard, "p-5", className)} {...props}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className={cn("mt-0.5 size-5 shrink-0", toneRing)} aria-hidden="true" />}
        <div className="min-w-0">
          {title && <div className="font-heading text-sm font-semibold text-foreground">{title}</div>}
          <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
});
InsightCard.displayName = "InsightCard";

/** Related calculator card — links to adjacent tools. */
const RelatedCalculatorCard = React.forwardRef(({ className, title, description, category, onClick, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    className={cn(
      baseCard,
      "group w-full p-5 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      className
    )}
    {...props}
  >
    {category && <div className="mb-2 text-xs font-medium uppercase tracking-wider text-subtle-foreground">{category}</div>}
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-heading font-semibold text-foreground">{title}</div>
        {description && <div className="mt-1 text-sm text-muted-foreground leading-relaxed line-clamp-2">{description}</div>}
      </div>
      <ChevronRight className="size-4 shrink-0 text-subtle-foreground transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
    </div>
  </button>
));
RelatedCalculatorCard.displayName = "RelatedCalculatorCard";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  ResultCard,
  InsightCard,
  RelatedCalculatorCard,
};